import { useQuery } from "@tanstack/react-query";
import { configService } from "@/app/services/configService";
import { DialogContent, DialogHeader } from "@/view/components/ui/dialog";
import { Spinner } from "@/view/components/ui/spinner";
import { PartnerType } from "@/app/utils/partners.enum";
import { ProductDetail } from "@/app/services/configService/getProductsConfig";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { TriStateEnum } from "@/app/entities/TriState";
import { ChevronRight, LinkIcon } from "lucide-react";
import { ExternalProductsResponse } from "@/app/services/externalConfigService/getProductsConfig";
import { withCdn } from "@/app/utils/withCdn";

const partnerNames: Partial<Record<PartnerType, string>> = {
  [PartnerType.PAN]: "PAN CLT",
  [PartnerType.CLT]: "Grandino CLT",
};

const getPartnerName = (partner: PartnerType) =>
  partnerNames[partner] ?? partner.charAt(0) + partner.slice(1).toLowerCase();

const partnerDescriptions: Record<PartnerType, string> = {
  [PartnerType.FGTS]: "Antecipação do saque-aniversário do FGTS",
  [PartnerType.CREFAZ]: "Crédito pessoal com débito na fatura de energia",
  [PartnerType.SALVCAR]: "Proteção veicular",
  [PartnerType.CREFISA]: "Crédito pessoal",
  [PartnerType.PAN]: "Consignado privado",
  [PartnerType.CLT]: "Consignado CLT",
};

interface ChooseSimulationModalProps {
  handleOpenLinkModal: () => void;
  onOptionSelect: (option: PartnerType) => void;
  externalProducts?: ExternalProductsResponse;
}

export default function ChooseSimulationModal({
  handleOpenLinkModal,
  onOptionSelect,
  externalProducts,
}: ChooseSimulationModalProps) {
  const { data: productsConfig, isLoading } = useQuery({
    queryKey: ["productsConfig"],
    queryFn: configService.getProductsConfig,
  });

  const simulationOptions = productsConfig
    ? (
      Object.entries(productsConfig.products) as [
        PartnerType,
        ProductDetail,
      ][]
    )
      .filter(([, detail]) => detail.state === TriStateEnum.FULL)
      .map(([productName]) => productName)
    : [];

  if (isLoading) {
    return (
      <DialogContent className="w-full max-w-3xl">
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      </DialogContent>
    );
  }

  const getProductData = (option: PartnerType) => {
    // busca do config, da api externa ou mock
    const productImage = productsConfig?.products[option]?.image;
    return {
      name: productsConfig?.products[option]?.label || externalProducts?.[option]?.title || getPartnerName(option),
      description: productsConfig?.products[option]?.subtitle || externalProducts?.[option]?.subtitle || partnerDescriptions[option],
      image: (productImage && withCdn(productImage)) || externalProducts?.[option]?.image || getPartnerImage(option),
      color: productsConfig?.products[option]?.color || externalProducts?.[option]?.color || getPartnerBgColor(option)
    }
  }
  return (
    <DialogContent className="w-full max-w-3xl p-6">
      <DialogHeader className="pb-2"></DialogHeader>

      <div className="flex flex-col gap-3">
        {simulationOptions.map((option) => (
          <Card
            key={option}
            option={option}
            name={getProductData(option).name}
            description={getProductData(option).description}
            onOptionSelect={(option) => option && onOptionSelect(option)}
            image={getProductData(option).image}
            color={getProductData(option).color}
          />
        ))}
        <Card
          key="link"
          option="link"
          name="Link"
          description="Criar link de autocontratação"
          onOptionSelect={() => handleOpenLinkModal()}
          icon={<LinkIcon />}
        />
      </div>
    </DialogContent>
  );
}

const Card = ({
  option,
  name,
  description,
  onOptionSelect,
  image,
  color,
  icon: Icon,
}: {
  option: PartnerType | 'link';
  name: string;
  description: string;
  onOptionSelect: (option?: PartnerType) => void;
  image?: string;
  color?: string;
  icon?: React.ReactNode;
}) => {

  return (
    <div
      key={option}
      onClick={() => onOptionSelect(option === 'link' ? undefined : option)}
      className="flex cursor-pointer items-center gap-4 rounded-lg border border-gray01 p-3 transition-all hover:border-primary hover:shadow-sm"
    >
      <div
        className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: option === 'link' ? 'gray' : color }}
      >
        {image ? (
          <img
            src={image}
            alt={option}
            className={`object-scale-down ${option === PartnerType.SALVCAR
              ? "h-10 w-auto px-2"
              : option === PartnerType.FGTS
                ? "h-16 w-auto"
                : "h-14 w-auto"
              }`}
          />
        ) : (
          <div>
            <span className="text-base font-semibold text-white">
              {Icon || option}
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <span className="text-base font-semibold text-black01">
          {name}
        </span>
        <span className="text-sm text-black03">
          {description}
        </span>
      </div>
      <ChevronRight className="ml-auto h-5 w-5 text-black04" />
    </div>
  )
}


